<p-toast [key]="toastKey" position="bottom-center">
	<ng-template let-message pTemplate="message">
		<div class="flex align-items-center gap-3 w-full">
			<i
				class="pi text-xl"
				[ngClass]="{
					'pi-check-circle': message.severity === 'success',
					'pi-info-circle': message.severity === 'info',
					'pi-times-circle': message.severity === 'error'
				}"
			></i>
			<span class="flex-1">{{ message.detail }}</span>
		</div>
	</ng-template>
</p-toast>

<div class="card">
	<div class="flex justify-content-between align-items-center mb-3">
		<h4 class="m-0">{{ labels.title }}</h4>
		<span *ngIf="canAdd" [pTooltip]="addDisabled ? disabledTooltip : ''" tooltipPosition="left">
			<p-button
				[label]="labels.addButton"
				icon="pi pi-plus"
				size="small"
				[disabled]="busy || addDisabled"
				(onClick)="openAddDialog()"
			/>
		</span>
	</div>

	<p-message
		*ngIf="readOnly && readOnlyMessage"
		severity="info"
		[text]="readOnlyMessage"
		styleClass="w-full justify-content-start mb-3"
	/>

	<app-skeleton-loader *ngIf="initialLoading" [count]="4" />

	<ng-container *ngIf="!initialLoading">
		<!-- All matching rows selected, across every page -->
		<div
			*ngIf="hasSelection && allMatchingSelected"
			class="flex flex-wrap align-items-center justify-content-between gap-2 bg-primary border-round px-3 py-2 mb-3"
			data-testid="bulk-access-all-banner"
		>
			<span class="font-medium">
				All {{ selection.length }} {{ selection.length === 1 ? labels.singular : labels.plural }} matching your
				current filters are selected.
			</span>
			<div class="flex align-items-center gap-3">
				<button type="button" class="p-link text-white underline font-semibold" (click)="clearSelection()">
					Clear selection
				</button>
				<p-button
					label="Remove Selected"
					severity="danger"
					size="small"
					[disabled]="busy || removeDisabled"
					(onClick)="removeSelected()"
				/>
			</div>
		</div>

		<!-- Some rows selected -->
		<div
			*ngIf="hasSelection && !allMatchingSelected"
			class="flex flex-wrap align-items-center justify-content-between gap-2 bg-primary-50 border-1 border-primary border-round px-3 py-2 mb-3"
			data-testid="bulk-access-bar"
		>
			<div class="flex flex-wrap align-items-center gap-2">
				<span class="font-bold">{{ selectionSummary }}</span>
				<ng-container *ngIf="canSelectAllMatching">
					<span class="text-color-secondary">·</span>
					<button
						type="button"
						class="p-link text-primary underline font-semibold"
						data-testid="bulk-access-select-all-matching"
						(click)="selectAllMatching()"
					>
						Select all {{ matchingCount }} matching
					</button>
				</ng-container>
				<span class="text-color-secondary">·</span>
				<button type="button" class="p-link text-primary underline font-semibold" (click)="clearSelection()">
					Clear selection
				</button>
			</div>
			<p-button
				label="Remove Selected"
				severity="danger"
				size="small"
				[disabled]="busy || removeDisabled"
				(onClick)="removeSelected()"
			/>
		</div>

		<p-table
			styleClass="standard-table"
			[value]="filteredRows"
			[dataKey]="idField"
			[selection]="selection"
			(selectionChange)="onSelectionChange($event)"
			[selectionPageOnly]="true"
			[rowSelectable]="rowSelectable"
			[paginator]="true"
			[rows]="pageSize"
			[rowsPerPageOptions]="rowsPerPageOptions"
			[(first)]="first"
			(onPage)="onPage($event)"
			[loading]="busy"
			responsiveLayout="scroll"
		>
			<ng-template pTemplate="header">
				<tr>
					<th *ngIf="canRemove" style="width: 3rem">
						<p-tableHeaderCheckbox [disabled]="busy || removeDisabled" />
					</th>
					<th *ngFor="let col of columns" [pSortableColumn]="col.field">
						{{ col.header }} <p-sortIcon [field]="col.field" />
					</th>
					<th *ngIf="canRemove" class="text-right">Action</th>
				</tr>
				<tr>
					<th *ngIf="canRemove"></th>
					<th *ngFor="let col of columns">
						<input
							*ngIf="col.filterPlaceholder"
							pInputText
							type="text"
							class="w-full"
							[placeholder]="col.filterPlaceholder"
							[value]="filters[col.field] || ''"
							(input)="onFilter(col.field, $any($event.target).value)"
						/>
					</th>
					<th *ngIf="canRemove"></th>
				</tr>
			</ng-template>
			<ng-template pTemplate="body" let-row>
				<tr>
					<td *ngIf="canRemove">
						<p-tableCheckbox [value]="row" [disabled]="busy || removeDisabled || !canRemoveRow(row)" />
					</td>
					<td *ngFor="let col of columns">
						<ng-container *ngIf="cellText(row, col) as text; else noValue">{{ text }}</ng-container>
						<ng-template #noValue><span class="text-color-secondary">—</span></ng-template>
					</td>
					<td *ngIf="canRemove" class="text-right">
						<span [pTooltip]="trashTooltip(row)" tooltipPosition="left">
							<p-button
								icon="pi pi-trash"
								severity="danger"
								[text]="true"
								[rounded]="true"
								[disabled]="hasSelection || busy || removeDisabled || !canRemoveRow(row)"
								(onClick)="removeRow(row)"
							/>
						</span>
					</td>
				</tr>
			</ng-template>
			<ng-template pTemplate="emptymessage">
				<tr>
					<td
						[attr.colspan]="columns.length + (canRemove ? 2 : 0)"
						class="text-center text-color-secondary py-4"
					>
						{{ rows.length ? 'No results match your filters.' : labels.empty }}
					</td>
				</tr>
			</ng-template>
		</p-table>
	</ng-container>
</div>

<app-bulk-access-add-dialog
	[visible]="addDialogVisible"
	(visibleChange)="addDialogVisible = $event"
	[header]="labels.addDialogHeader"
	[rows]="availableRows"
	[columns]="addColumnsResolved"
	[idField]="idField"
	[loading]="availableLoading"
	[saving]="busy"
	[emptyMessage]="'No ' + labels.plural + ' available to add.'"
	(submitted)="onAddSubmitted($event)"
/>

<app-bulk-access-confirm-dialog
	[visible]="!!confirm"
	[header]="confirm?.header ?? ''"
	[message]="confirm?.message ?? ''"
	[items]="confirm?.items ?? []"
	[acceptLabel]="confirm?.acceptLabel ?? 'Confirm'"
	[destructive]="confirm?.action === 'remove'"
	[phase]="confirmPhase"
	[undoable]="confirm?.undoable ?? true"
	[secondsLeft]="secondsLeft"
	[totalSeconds]="undoSeconds"
	[countdownNote]="countdownNote"
	[sendingLabel]="sendingLabel"
	[error]="confirmError"
	(accept)="onConfirmAccept()"
	(cancel)="onConfirmCancel()"
	(undo)="undo()"
	(retry)="onConfirmRetry()"
/>

<!-- DOVE-2676 — leaving mid-countdown (in-app navigation, i.e. the browser Back button). -->
<p-dialog
	[visible]="leavePromptVisible"
	[modal]="true"
	[closable]="false"
	[closeOnEscape]="false"
	[draggable]="false"
	[resizable]="false"
	appendTo="body"
	[style]="{ width: '26rem' }"
	[breakpoints]="{ '640px': '95vw' }"
>
	<ng-template pTemplate="header">
		<div class="flex align-items-center gap-2">
			<i class="pi pi-exclamation-triangle text-xl text-orange-500"></i>
			<span class="font-bold text-lg">Leave this page?</span>
		</div>
	</ng-template>
	<p class="m-0 line-height-3" data-testid="bulk-access-leave-message">{{ leaveMessage }}</p>
	<ng-template pTemplate="footer">
		<p-button label="Leave without saving" severity="danger" [outlined]="true" (onClick)="onLeaveConfirmed()" />
		<p-button label="Stay" (onClick)="onLeaveStay()" />
	</ng-template>
</p-dialog>
